import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { SECURE_API } from 'src/api/api';
import {
  PERSISTENT_KEY_ACCESS_TOKEN,
  PERSISTENT_KEY_AUTH_USER,
  PERSISTENT_KEY_REFRESH_TOKEN,
} from 'src/common/constants/persistentStorageKeys';
import { ILoginUser, IUser } from 'src/interfaces/User';
import { IFormProfileUser } from 'src/interfaces/User';

/**
 * A helper to create a Context and Provider with no upfront default value, and
 * without having to check for undefined all the time.
 */
function createCtx<A extends object | null>() {
  const ctx = createContext<A | undefined>(undefined);
  const useCtx = () => {
    const c = useContext(ctx);
    if (c === undefined) throw new Error('useAuth must be inside a Provider with a value');
    return c;
  };
  return [useCtx, ctx.Provider] as const; // 'as const' makes TypeScript infer a tuple
}

interface AuthCtxInterface {
  accessToken?: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  refreshToken?: string;
  signIn: (userToLogIn: ILoginUser, callback?: () => void) => Promise<void>;
  signOut: (callback?: () => void) => Promise<void>;
  signUp: (userToCreate: ILoginUser, callback?: () => void) => void;
  register: (userToRegister: { name: string; email: string; password: string; dateFormat: string; timeFormat: string }) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  update: (userToUpdate: IFormProfileUser, callback?: () => void) => Promise<void>;
  user?: IUser;
}

// Provider hook that creates auth object and handles state
const useProvideAuth = () => {
  const lsUser = localStorage.getItem(PERSISTENT_KEY_AUTH_USER);

  const parsedLsUser = lsUser ? JSON.parse(lsUser) : null;
  const [user, setUser] = useState<IUser | undefined>(parsedLsUser || undefined);
  const initialRoleId = parsedLsUser?.role?.roleId;
  const [isSuperAdmin, setIsSuperAdmin] = useState(initialRoleId === 0);
  const [isAdmin, setIsAdmin] = useState(initialRoleId === 1 || initialRoleId === 0);
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem(PERSISTENT_KEY_ACCESS_TOKEN) || undefined,
  );
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem(PERSISTENT_KEY_REFRESH_TOKEN) || undefined,
  );

  useEffect(() => {
    if (user) {
      localStorage.setItem(PERSISTENT_KEY_AUTH_USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(PERSISTENT_KEY_AUTH_USER);
    }
  }, [user]);

  useEffect(() => {
    if (accessToken) {
      localStorage.setItem(PERSISTENT_KEY_ACCESS_TOKEN, accessToken);
    } else {
      localStorage.removeItem(PERSISTENT_KEY_ACCESS_TOKEN);
    }
  }, [accessToken]);

  useEffect(() => {
    if (refreshToken) {
      localStorage.setItem(PERSISTENT_KEY_REFRESH_TOKEN, refreshToken);
    } else {
      localStorage.removeItem(PERSISTENT_KEY_REFRESH_TOKEN);
    }
  }, [refreshToken]);

  // Update role flags when user changes
  useEffect(() => {
    if (user) {
      const roleId = user.role?.roleId;
      setIsSuperAdmin(roleId === 0);
      setIsAdmin(roleId === 1 || roleId === 0);
    } else {
      setIsSuperAdmin(false);
      setIsAdmin(false);
    }
  }, [user]);

  // Listen for external logout events (e.g., from API interceptor on auth error)
  useEffect(() => {
    const handleAuthLogout = () => {
      // Auth data was cleared externally - sync state
      setUser(undefined);
      setAccessToken(undefined);
      setRefreshToken(undefined);
      setIsSuperAdmin(false);
      setIsAdmin(false);
    };

    const handleUserUpdated = (e: Event) => {
      const updatedUser = (e as CustomEvent<IUser>).detail;
      setUser(updatedUser);
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    window.addEventListener('auth:userUpdated', handleUserUpdated);
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
      window.removeEventListener('auth:userUpdated', handleUserUpdated);
    };
  }, []);

  const signIn = async (userToLogIn: ILoginUser & { keepSignedIn?: boolean }, callback?: () => void) => {
    return SECURE_API.post<{
      user: IUser;
      accessToken: string;
      refreshToken: string;
    }>('users/login', userToLogIn).then(({ data }) => {
      if (data) {
        setUser(data.user);
        // Set role flags directly from user data (doesn't require rolesById to be loaded)
        const roleId = data.user.role?.roleId;
        setIsSuperAdmin(roleId === 0);
        setIsAdmin(roleId === 1 || roleId === 0);
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);
      } else {
        setUser(undefined);
      }
      callback && callback();
    });
  };

  const signOut = async (callback?: () => void) => {
    return SECURE_API.post<null>('users/logout', {
      token: accessToken,
      'refresh-token': refreshToken,
    })
      .then(() => {
        // Clear auth data on successful logout
        setUser(undefined);
        setAccessToken(undefined);
        setRefreshToken(undefined);
        callback && callback();
      });
  };

  const signUp = (userToCreate: ILoginUser, callback?: () => void) => {
    SECURE_API.post<IUser>('users/create', userToCreate, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then(({ data }) => {
        if (data) {
          setUser(data);
        } else {
          setUser(undefined);
        }
        callback && callback();
      })
      .catch(error => console.log({ error }));
  };

  const register = async (userToRegister: {
    name: string;
    email: string;
    password: string;
    dateFormat: string;
    timeFormat: string;
  }) => {
    await SECURE_API.post<IUser>('/auth/register', userToRegister);
  };

  const verifyEmail = async (token: string) => {
    await SECURE_API.post<void>('/auth/verify-email', { token });
  };

  const update = async (userToUpdate: IFormProfileUser, callback?: () => void) => {
    return SECURE_API.put<IUser>(`users/${userToUpdate.id}`, userToUpdate, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(({ data }) => {
      if (data) {
        setUser(data);
      } else {
        setUser(undefined);
      }
      callback && callback();
    });
  };

  return {
    user,
    accessToken,
    isAdmin,
    isSuperAdmin,
    refreshToken,
    signIn,
    signUp,
    signOut,
    register,
    verifyEmail,
    update,
  };
};

export const [useAuth, AuthCtxProvider] = createCtx<AuthCtxInterface>();

type ProvideAuthProps = { children: ReactNode };

export const ProvideAuth = ({ children }: ProvideAuthProps) => {
  const auth = useProvideAuth();
  return <AuthCtxProvider value={auth}>{children}</AuthCtxProvider>;
};
